/**
 * useDebriefingPoller — poll les débriefings pédagogiques créés par le
 * formateur et pousse un message inbox pour chaque nouveau.
 *
 * 11/02/2026 (Phase 3 v2 — Superpouvoirs formateur).
 *
 * Cadence : mount + toutes les 10 min. Message inbox unique par débriefing
 * (dédup via `debriefingIdSeen` set). Le clic sur le message ouvre la
 * modale de réponse (voir `DebriefingResponseModal`).
 */
import { useEffect, useRef } from 'react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 10 * 60 * 1000;

async function fetchPending(licenseKey) {
  if (!licenseKey || !BACKEND) return [];
  try {
    const resp = await fetch(
      `${BACKEND}/api/master/debriefings/pending?license_key=${encodeURIComponent(licenseKey)}`,
    );
    if (!resp.ok) return [];
    const data = await resp.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch (_) {
    return [];
  }
}

async function drain(licenseKey) {
  const state = useGameStore.getState();
  if (!state.gameStarted) return;
  const items = await fetchPending(licenseKey);
  if (items.length === 0) return;

  const seen = new Set(state.debriefingIdSeen || []);
  const newItems = items.filter((d) => !seen.has(d.id));
  if (newItems.length === 0) return;

  // Enregistre les IDs vus + expose la liste pendings dans le store pour la
  // modale de réponse.
  const nextSeen = [...seen, ...newItems.map((d) => d.id)];
  useGameStore.setState({
    debriefingIdSeen: nextSeen,
    pendingDebriefings: items, // toujours à jour, même après re-poll
  });

  // Push un message inbox par débriefing.
  if (typeof state.pushCustomMessage === 'function') {
    for (const d of newItems) {
      try {
        state.pushCustomMessage({
          senderType: 'supervisor',
          senderName: d.master_holder || 'Responsable pédagogique',
          tone: 'urgent',
          kind: 'info',
          subject: `Débriefing pédagogique : ${d.title}`,
          body:
            (d.intro || 'Votre formateur souhaite recueillir votre analyse.') +
            `\n\n${d.questions?.length || 0} question${(d.questions?.length || 0) > 1 ? 's' : ''} vous attendent. Ouvrez la modale via le bouton en bas de l'écran.`,
          ttlDays: 30,
          payload: { debriefingId: d.id },
        });
      } catch (err) {
        console.warn('[useDebriefingPoller] pushCustomMessage failed:', err);
      }
    }
  }
}

export default function useDebriefingPoller() {
  const licenseKey = useLicenseStore((s) => s.licenseKey);
  const monthRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!licenseKey) return undefined;
    const initial = useGameStore.getState();
    if (initial.gameStarted && !startedRef.current) {
      startedRef.current = true;
      monthRef.current = `${initial.year}-${initial.month}`;
      drain(licenseKey);
    }
    const unsubscribe = useGameStore.subscribe((state) => {
      const tag = `${state.year}-${state.month}`;
      if (state.gameStarted && !startedRef.current) {
        startedRef.current = true;
        monthRef.current = tag;
        drain(licenseKey);
        return;
      }
      if (state.gameStarted && monthRef.current && tag !== monthRef.current) {
        monthRef.current = tag;
        drain(licenseKey);
      }
    });
    return unsubscribe;
  }, [licenseKey]);

  useEffect(() => {
    if (!licenseKey) return undefined;
    const id = setInterval(() => {
      const s = useGameStore.getState();
      if (s.gameStarted) drain(licenseKey);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [licenseKey]);
}
